import { cn } from "@/lib/utils";
import React from "react";
import type { FormulaScope } from "./api";
import { fieldScope, lex, type Token } from "./lexer";
import { fieldStyle } from "./style";

const TOKEN_CLASSES: Partial<Record<Token["kind"], string>> = {
    string: "text-green-700",
    number: "font-semibold text-teal-700",
    duration: "font-semibold text-teal-700",
    op: "font-bold text-gray-900",
    lparen: "text-gray-500",
    rparen: "text-gray-500",
    comma: "text-gray-500",
    error: "text-red-700"
};

/**
 * A formula coloured as the editor shows it: fields in their object's colour, functions in
 * violet, numbers and durations in teal, texts in green. `errors` are underlined. Every
 * character of `text` is rendered as is (the editor lays this under its textarea).
 */
export function FormulaText({
    text,
    scopes,
    functions,
    errors = [],
    className
}: {
    text: string;
    scopes: FormulaScope[];
    /** Known function names: a word followed by « ( » is coloured only when it is one. */
    functions?: Set<string>;
    errors?: { start: number; end: number }[];
    className?: string;
}) {
    const tokens = lex(text);
    const parts: React.ReactNode[] = [];
    let position = 0;
    tokens.forEach((token, index) => {
        if (token.start > position) parts.push(text.slice(position, token.start));
        const bad = errors.some(error => error.start < token.end && error.end > token.start);
        let className = TOKEN_CLASSES[token.kind];
        let style: React.CSSProperties | undefined;
        if (token.kind === "field") {
            // Being typed: not yet a field of any record.
            style = token.open ? fieldStyle(null) : fieldStyle(fieldScope(token.content ?? "", scopes)?.color);
        } else if (token.kind === "ident" && tokens[index + 1]?.kind === "lparen" && (!functions || functions.has(token.text.toUpperCase()))) {
            className = "font-bold text-violet-700";
        }
        parts.push(
            <span key={token.start} data-token={token.kind} className={cn(className, bad && "underline decoration-red-600 decoration-wavy underline-offset-4")} style={style}>
                {token.text}
            </span>
        );
        position = token.end;
    });
    if (position < text.length) parts.push(text.slice(position));
    return <span className={className}>{parts}</span>;
}
